/**
 * Item Shop (Masterprompt Abschnitt 66).
 *
 * VERBINDLICH:
 * - 4 Itemslots,
 * - normale Items haben 3 Stufen (250, dann +550, dann +1000 Gold),
 * - Verkauf bringt 65 % des eingesetzten Goldes zurueck,
 * - Consumables belegen KEINEN Slot,
 * - Revive Totem kostet rund 2.000 Gold, belegt einen Slot, hoechstens eines.
 *
 * Welche Items es gibt und was sie bewirken, ist Produktentscheidung vom
 * 24.09.2026. Die ZAHLEN der Wirkungen sind TUNING_PLACEHOLDER.
 */

import { ITEM_TIER_COST, REVIVE_TOTEM_COST } from './economy.js';

/**
 * Goldkosten der drei Stufen: Kauf, erster Ausbau, zweiter Ausbau.
 *
 * Die Werte stehen in economy.ts - hier stehen sie nur der Reihe nach, damit
 * sich eine Stufe direkt nachschlagen laesst.
 */
export const ITEM_STAGE_COSTS: readonly number[] = [
  ITEM_TIER_COST.t1,
  ITEM_TIER_COST.t2,
  ITEM_TIER_COST.t3,
  ITEM_TIER_COST.t4,
];

/** Hoechste Stufe eines normalen Items. */
export const MAX_ITEM_STAGE = ITEM_STAGE_COSTS.length;

/**
 * Lieferzeit in Sekunden - seit dem 09.10.2026 null.
 *
 * Sie gab es seit dem 24.09.2026 mit genau einer Begruendung: "Gekauft
 * wird ueberall, aber das Item wirkt erst nach dieser Zeit [...] und
 * trotzdem muss niemand quer ueber die Lane zurueck zum Portal laufen."
 * (6 statt 4 Sekunden ab dem 01.10.2026.)
 *
 * Mit dem Sanctuary muss man genau das: Eingekauft wird NUR dort
 * (GAME_DESIGN Abschnitt 5f). Der Weg ist damit der Preis geworden, und
 * eine zusaetzliche Wartezeit wuerde denselben Kauf zweimal bestrafen.
 *
 * Die Technik dahinter (deliverySecondsRemaining) bleibt stehen. Sie
 * kostet nichts, und sie ist der Weg zurueck, falls sich die
 * Shop-Entscheidung noch einmal dreht. Ein Test haelt sie wach: "Ein
 * Totem, das noch unterwegs ist, rettet nicht."
 * TUNING_PLACEHOLDER.
 */
export const ITEM_DELIVERY_SECONDS = 0;

/**
 * Wirkung eines Items auf den Hero.
 *
 * Alle Werte sind Zuschlaege und wirken additiv. Was ein Item nicht angibt,
 * bleibt 0 - deshalb sind alle Felder freiwillig.
 */
export interface ItemEffects {
  /*
   * --- Anteile auf die Werte des Heroes (Produktentscheidung 28.09.2026) ---
   *
   * 0,25 heisst 25 Prozent mehr. Gerechnet wird auf den AKTUELLEN Wert des
   * Heroes, also mit Levelwachstum und Talenten - ein Item bleibt so auch
   * spaet im Match relevant.
   *
   * Warum Anteile: "+180 Leben" ist fuer den Vanguard mit 900 Grundleben
   * derselbe Zuschlag wie fuer den Arcanist mit 620. "+25 Prozent" ist es
   * nicht - und damit lohnt sich ein Lebens-Item endlich dort, wo Leben
   * zaehlt.
   */

  /** Anteil auf das maximale Leben. */
  maxHpRatio?: number;
  /** Anteil auf den Angriffsschaden. */
  attackDamageRatio?: number;
  /** Anteil auf die maximale Ressource (Mana oder Energy). */
  maxManaRatio?: number;
  /** Anteil auf das Tempo. */
  moveSpeedRatio?: number;

  /*
   * --- Feste Werte ---
   *
   * Ruestung bleibt fest: Die Heroes haben keine Grundruestung, ein Anteil
   * waere also immer null. Sie wirkt ohnehin ueber eine Kurve, die selbst in
   * "X Prozent weniger Schaden" muendet, und die Magieresistenz IST eine
   * Prozentzahl. Ironhide ist damit das eine Item, das fuer jeden Hero
   * gleich viel wert ist - ein bewusster Gegenpol.
   */

  /** Ruestung gegen physischen Schaden. */
  armor?: number;
  /** Magieresistenz. */
  magicResistance?: number;
  /**
   * Zusaetzliche Ressource pro Sekunde.
   *
   * Bleibt fest: Die Grundregeneration ist fuer alle Heroes dieselbe Zahl -
   * ein Anteil davon waere fuer alle derselbe Zuschlag und nur umstaendlicher.
   */
  manaRegen?: number;
  /** Zusaetzliche Lebensregeneration pro Sekunde, als Anteil des Maximums. */
  hpRegenRatio?: number;
  /** Verkuerzung aller Abklingzeiten, 0 bis 1. */
  cooldownReduction?: number;
  /** Heilung je erledigtem Creep, als Anteil des maximalen Lebens. */
  healOnKillRatio?: number;

  // --- Wirkung auf die eigene Line (Produktentscheidung vom 27.09.2026) ---
  //
  // Bis v0.35 machte jedes Item nur den Hero staerker. Wellen, Defender und
  // Portal sind aber der Kern des Spiels - deshalb gibt es jetzt Items, die
  // dorthin wirken. Der Shop wird damit zur Frage: ich oder meine Linie?
  //
  // Alle drei sind Anteile: 0,3 heisst 30 Prozent mehr.

  /** Mehr Leben fuer die Creeps, die DIESER Spieler sendet. */
  sentCreepHpRatio?: number;
  /** Mehr Income je Tick. */
  incomeRatio?: number;
  /**
   * Mehr Schaden fuer die Creeps, die DIESER Spieler sendet.
   *
   * Hiess bis zum 08.10.2026 defenderStrengthRatio und gab den vier
   * gekauften Defendern mehr Leben UND Schaden. Mit dem Defense System
   * ist der Traeger weggefallen; das Item bleibt und wirkt jetzt auf die
   * eigene Armee (GAME_DESIGN Abschnitt 10).
   */
  sentCreepDamageRatio?: number;
}

/**
 * Grobe Einordnung eines Items - nur fuer die Anzeige.
 *
 * Die Referenz des Item-Menues zeigt diese Gruppen als Reiter. Reiter wurden
 * es bei uns nicht: Sechs Items plus Totem auf fuenf Reiter verteilt haette
 * je ein bis zwei Karten je Reiter ergeben - dieselbe Leere, die den ALL-Reiter
 * im Send-Wave-Fenster gekostet hat (Produktentscheidung vom 27.09.2026).
 * Stattdessen steht die Gruppe als Plakette auf der Karte.
 */
export type ItemCategory = 'offense' | 'magic' | 'defense' | 'resource' | 'utility';

export interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  /** Grobe Einordnung fuer die Anzeige. */
  category: ItemCategory;
  /**
   * Wirkung je Stufe. Die Stufen ersetzen einander - Stufe 2 enthaelt also
   * bereits alles, was Stufe 1 konnte.
   */
  stages: readonly ItemEffects[];
  /** Besondere Items wie das Revive Totem haben nur eine Stufe. */
  unique?: boolean;
  /**
   * Eigener Preisfaktor auf alle Stufen. 1 = normale Preise.
   *
   * Gibt es, damit ein einzelnes Item teurer sein kann als der Rest, ohne
   * dass die Preistabelle fuer alle anderen kippt (Produktentscheidung vom
   * 28.09.2026: Das Trade Seal wirkt ueber die ganze Restlaufzeit und
   * soll deshalb mehr kosten).
   */
  costFactor?: number;
  /** Fester Preis statt der Stufenkosten. Nur fuer besondere Items. */
  fixedCost?: number;
}

/**
 * Die sechs normalen Items (Produktentscheidung vom 24.09.2026).
 *
 * Je eines fuer Leben, Schutz, Schaden, Skills, Ausdauer und Tempo. Damit hat
 * jede Rolle etwas zu kaufen, ohne dass der Shop unuebersichtlich wird.
 */
export const ITEMS: readonly ItemDefinition[] = [
  {
    id: 'bulwark_plate',
    category: 'defense',
    name: 'Bulwark Plate',
    description: 'More health, as a share of your own. Tanks gain the most.',
    // 25/60/110/180 Prozent - beim Durchschnittshero (710 Leben) sind das rund
    // die 180/420/800/1300, die hier bis zum 28.09.2026 fest standen.
    stages: [{ maxHpRatio: 0.25 }, { maxHpRatio: 0.6 }, { maxHpRatio: 1.1 }, { maxHpRatio: 1.8 }],
  },
  {
    id: 'ironhide',
    category: 'defense',
    name: 'Ironhide',
    description: 'Armor and magic resist against every kind of damage.',
    stages: [
      { armor: 6, magicResistance: 4 },
      { armor: 14, magicResistance: 10 },
      { armor: 26, magicResistance: 18 },
      { armor: 42, magicResistance: 30 },
    ],
  },
  {
    id: 'war_blade',
    category: 'offense',
    name: 'War Blade',
    description: 'More attack damage, as a share of your own.',
    // 35/90/170/285 Prozent - beim Durchschnittshero (33,5 Schaden) rund die
    // 12/30/58/95, die hier vorher fest standen.
    stages: [
      { attackDamageRatio: 0.35 },
      { attackDamageRatio: 0.9 },
      { attackDamageRatio: 1.7 },
      { attackDamageRatio: 2.85 },
    ],
  },
  {
    id: 'rune_stone',
    category: 'magic',
    name: 'Rune Stone',
    description:
      'More mana, as a share of your own, plus regen. From stage 3 also shorter cooldowns.',
    // 15/40/80/130 Prozent - beim Durchschnittshero (360 Ressource) rund die
    // 60/150/280/460 von vorher. Die Regeneration bleibt fest: Ihr Grundwert
    // ist fuer alle Heroes derselbe.
    /*
     * Regeneration 2/5/9/15 -> 1/2/4/6 (Produktentscheidung vom 05.10.2026).
     *
     * Stufe 4 gab dem Arcanist 6 + 15 = 21 Regeneration gegen 14,6
     * Verbrauch: Er konnte damit UNENDLICH wirken. Ein Item darf eine
     * Ressource teurer erkaufen, aber nicht abschalten.
     *
     * Der Vorrat (maxManaRatio) bleibt unveraendert - der Stein bleibt das
     * Manaitem, er hebt die Regel nur nicht mehr auf.
     */
    stages: [
      { maxManaRatio: 0.15, manaRegen: 1 },
      { maxManaRatio: 0.4, manaRegen: 2 },
      { maxManaRatio: 0.8, manaRegen: 4, cooldownReduction: 0.15 },
      { maxManaRatio: 1.3, manaRegen: 6, cooldownReduction: 0.25 },
    ],
  },
  {
    id: 'life_stone',
    category: 'resource',
    name: 'Life Stone',
    description: 'Constant health regen. From stage 3 every kill heals you.',
    stages: [
      { hpRegenRatio: 0.004 },
      { hpRegenRatio: 0.009 },
      { hpRegenRatio: 0.015, healOnKillRatio: 0.02 },
      { hpRegenRatio: 0.024, healOnKillRatio: 0.035 },
    ],
  },
  {
    id: 'march_boots',
    category: 'utility',
    name: 'March Boots',
    description: 'More move speed, as a share of your own - closer to the next wave.',
    // 10/20/30/50 Prozent - beim Durchschnittshero (15,9 Tempo) rund die
    // 1,5/3/5/8 von vorher.
    stages: [
      { moveSpeedRatio: 0.1 },
      { moveSpeedRatio: 0.2 },
      { moveSpeedRatio: 0.3 },
      { moveSpeedRatio: 0.5 },
    ],
  },

  /*
   * Die drei Line-Items (Produktentscheidung vom 27.09.2026).
   *
   * Sie machen nicht den Hero staerker, sondern das, was er verteidigt und
   * schickt. Damit hat spaetes Gold eine Richtung: in die eigene Linie.
   * ZAHLEN sind TUNING_PLACEHOLDER.
   */
  {
    id: 'war_banner',
    category: 'offense',
    name: 'War Banner',
    description: 'The creeps you send take more punishment.',
    // 8/15/25/40 statt 8/18/30/45 (01.10.2026): Die erste Stufe bleibt, die
    // oberen drei werden flacher.
    stages: [
      { sentCreepHpRatio: 0.08 },
      { sentCreepHpRatio: 0.15 },
      { sentCreepHpRatio: 0.25 },
      { sentCreepHpRatio: 0.4 },
    ],
  },
  {
    id: 'trade_seal',
    category: 'resource',
    name: 'Trade Seal',
    description: 'More income on every payout.',
    /*
     * Jede Stufe kostet die Haelfte mehr als bei den anderen Items
     * (Produktentscheidung vom 28.09.2026). Income wirkt ueber die ganze
     * Restlaufzeit - das Siegel bezahlt sich als einziges Item mit der Zeit
     * selbst.
     *
     * Die Prozente sind HALBIERT (Produktentscheidung vom 29.09.2026): vorher
     * 10/15/25/35.
     *
     * Grund aus einer Messung ueber 1.280 Matches mit fuenf Plaenen, die sich
     * NUR in der Einkaufsliste unterschieden - gleicher Hero, gleiche
     * Punktereihenfolge, gleiches Talent, gleicher Capstone:
     *
     *   kein Line-Item      35,0 %   3.152 Gold je Minute
     *   nur Wachstein       51,3 %   3.152
     *   nur Kriegsbanner    51,9 %   3.110
     *   nur Handelssiegel   76,9 %   3.550
     *   alle drei           96,3 %   3.475
     *
     * Das Siegel allein war mehr wert als die anderen zwei zusammen. Es bringt
     * 12,6 Prozent mehr Gold je Minute - und dafuer 42 Punkte Siegquote. Die
     * anderen beiden veraendern die Wirtschaft ueberhaupt nicht; sie machen aus
     * demselben Gold nur mehr. Ziel der Halbierung ist, dass das Siegel etwa so
     * stark ist wie sie, also rund plus 16 Punkte.
     *
     * VORMERKUNG: Die eigentliche Ursache liegt tiefer. Ueber alle 16 Plaene
     * trennt eine Stufe von rund 10 Prozent Gold je Minute die Gruppe, die fast
     * immer gewinnt (77 bis 96 Prozent), von der, die meistens verliert (21 bis
     * 52). Solange das so steil ist, ist JEDES Income-Item zu stark. Siehe
     * BALANCE.md.
     */
    costFactor: 1.5,
    stages: [
      { incomeRatio: 0.05 },
      { incomeRatio: 0.08 },
      { incomeRatio: 0.13 },
      { incomeRatio: 0.18 },
    ],
  },
  {
    id: 'watch_stone',
    category: 'defense',
    name: 'Watch Stone',
    description: 'The Bound you send hit harder.',
    /*
     * UMGEWIDMET am 08.10.2026 (GAME_DESIGN Abschnitt 10).
     *
     * Vorher: mehr Leben und Schaden fuer die vier gekauften Defender.
     * Die gibt es nicht mehr - also wirkt der Stein jetzt auf die eigene
     * Armee, und nur auf deren SCHADEN.
     *
     * Die Stufen 10/25/40/60 sind unveraendert uebernommen
     * (Produktentscheidung vom 08.10.2026: lieber messen als raten).
     * Achtung: Das ist vermutlich deutlich staerker als vorher - es wirkt
     * auf eine ganze Welle statt auf hoechstens vier Einheiten. Die Zahl
     * gehoert nach dem Bot-Umbau nachgemessen.
     */
    stages: [
      { sentCreepDamageRatio: 0.1 },
      { sentCreepDamageRatio: 0.25 },
      { sentCreepDamageRatio: 0.4 },
      { sentCreepDamageRatio: 0.6 },
    ],
  },
];

/**
 * Revive Totem (Masterprompt Abschnitt 66).
 *
 * Belegt einen Slot, hoechstens eines. Bei Tod wird es verbraucht: Nach zwei
 * Sekunden erscheint der Hero am Portal, die normale Respawnzeit entfaellt.
 * Faellt das Portal, ist das Match trotzdem verloren.
 */
export const REVIVE_TOTEM: ItemDefinition = {
  id: 'revive_totem',
  category: 'utility',
  name: 'Revive Totem',
  description: 'Single use: on death you return at your portal after 2 seconds.',
  stages: [{}],
  unique: true,
  fixedCost: REVIVE_TOTEM_COST,
};

/** Respawnzeit mit Revive Totem, in Sekunden. VERBINDLICH. */
export const REVIVE_TOTEM_RESPAWN_SECONDS = 2;

/** Alles, was einen Itemslot belegt. */
export const SLOT_ITEMS: readonly ItemDefinition[] = [...ITEMS, REVIVE_TOTEM];

/**
 * Ein Consumable. Belegt KEINEN Itemslot (Masterprompt Abschnitt 66).
 *
 * Wirkung ueber Zeit statt sofort: Ein Schluck mitten im Kampf soll eine
 * Entscheidung sein und nicht jeden Treffer ungeschehen machen.
 */
export interface ConsumableDefinition {
  id: string;
  name: string;
  cost: number;
  /** Was aufgefuellt wird. */
  restores: 'hp' | 'mana';
  /** Anteil des Maximums, der insgesamt zurueckkommt. */
  ratio: number;
  /** Ueber wie viele Sekunden. */
  durationSeconds: number;
  /** Wie viele man hoechstens mit sich traegt. */
  maxCharges: number;
}

/** Alle Consumables. TUNING_PLACEHOLDER bis auf "belegt keinen Slot". */
export const CONSUMABLES: readonly ConsumableDefinition[] = [
  {
    id: 'health_potion',
    name: 'Health Potion',
    cost: 120,
    restores: 'hp',
    ratio: 0.3,
    durationSeconds: 8,
    maxCharges: 2,
  },
  {
    id: 'mana_potion',
    name: 'Mana Potion',
    cost: 100,
    restores: 'mana',
    ratio: 0.4,
    durationSeconds: 8,
    maxCharges: 2,
  },
];

/** Gemeinsame Abklingzeit nach dem Trinken, in Sekunden. TUNING_PLACEHOLDER. */
export const CONSUMABLE_COOLDOWN_SECONDS = 15;

const SLOT_ITEMS_BY_ID = new Map(SLOT_ITEMS.map((item) => [item.id, item]));
const CONSUMABLES_BY_ID = new Map(CONSUMABLES.map((item) => [item.id, item]));

export function getItem(id: string): ItemDefinition | undefined {
  return SLOT_ITEMS_BY_ID.get(id);
}

export function getConsumable(id: string): ConsumableDefinition | undefined {
  return CONSUMABLES_BY_ID.get(id);
}
